<template>
  <div class="luna-face-step">
    <div class="p-1 pb-0">
      <!-- Master Account -->
      <validation-provider
        #default="validationContext"
        name="master account"
        rules="required"
      >
        <b-form-group
          :label="$tc('luna.master_account.title', 1)"
          label-for="master account"
          :state="getValidationState(validationContext)"
        >
          <v-select
            v-model="masterAccount"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="lunaMasterAccountOptions"
            :reduce="(val) => val.value"
            input-id="master_account"
            :clearable="false"
            :disabled="itemData.id"
          />
          <b-form-invalid-feedback :state="getValidationState(validationContext)">
            {{ validationContext.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>

      <b-form-group :label="$t('luna.face.image')" label-for="dropzone">
        <vue-dropzone
          v-show="!previewUrl"
          id="dropzone"
          ref="dropzoneImage"
          :options="dropzoneOptions"
          class="dropzone-main"
          @vdropzone-file-added="addFileImage"
          @vdropzone-removed-file="removeFileImage"
          @vdropzone-sending="sendingFileImage"
          @vdropzone-success="success"
          @vdropzone-error="error"
        />

        <div v-if="previewUrl" class="luna-preview">
          <div class="luna-preview__frame">
            <img
              ref="previewImg"
              :src="previewUrl"
              :alt="fileName"
              class="luna-preview__img"
              @load="renderLandmarks"
            />
            <div v-if="isExtracting" class="luna-preview__overlay">
              <b-spinner variant="light" small />
              <span class="luna-preview__overlay-text">
                {{ $t('luna.face_search.processing') }}
              </span>
            </div>
            <div
              v-else-if="extractionDone && !hasFaces"
              class="luna-preview__overlay luna-preview__overlay--warning"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="30"
                class="luna-preview__overlay-icon"
              />
              <span class="luna-preview__overlay-text">
                {{ $t('luna.face_search.no_faces') }}
              </span>
            </div>
          </div>

          <div class="luna-preview__meta">
            <span class="luna-preview__name" :title="fileName">{{ fileName }}</span>
            <b-button
              variant="outline-danger"
              size="sm"
              class="text-capitalize"
              :disabled="isExtracting"
              @click="clearImage"
            >
              <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
              {{ $t('actions.delete') }}
            </b-button>
          </div>
        </div>
      </b-form-group>
    </div>

    <!-- BODY -->
    <validation-observer
      #default="{ handleSubmit }"
      ref="refFormObserver"
      v-for="face in faces"
      :key="face.sample_id"
    >
      <!-- Form -->
      <b-form
        class="luna-face-card"
        @submit.prevent="handleSubmit(() => onSubmit(face))"
      >
        <b-img :src="face.url_image" class="luna-face-card__img" />

        <div class="luna-face-card__fields">
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name face"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('luna.face.name')"
              label-for="name face"
            >
              <b-form-input
                id="name"
                v-model="face.user_data"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- List -->
          <validation-provider
            v-if="!itemData.id"
            #default="validationContext"
            name="list"
            rules="required"
          >
            <b-form-group
              :label="$t('luna.list.title')"
              label-for="list"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="face.list"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="lunaListOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="list"
                multiple
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-1 justify-content-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="text-capitalize"
              type="submit"
            >
              {{ $t('luna.face_search.search_matches') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";
import { watch } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, onBeforeUnmount } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    vueDropzone: vue2Dropzone,
    BFormCheckbox,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    lunaListOptions: {
      type: Array,
      required: true,
    },
    lunaMasterAccountOptions: {
      type: Array,
      required: true,
    },
    imageUrl: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const listArray = ref([]);
    const masterAccount = ref();

    const { t } = useI18nUtils();
    const blankData = {
      id_luna_master_account: null,
      list: null,
      name: null,
      file: null,
      external_id: null,
      attributes: null,
    };

    const dropzoneOptions = ref({
      url: `${window.__env.VUE_APP_BASE_URL}/v1/services/facial-recognition/luna/extract/luna-face`,
      paramName: "file",
      autoProcessQueue: false,
      thumbnailMethod: "crop",
      addRemoveLinks: false,
      thumbnailWidth: null,
      thumbnailHeight: null,
      dictRemoveFile: t("actions.delete"),
      acceptedFiles: "image/*",
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      dictDefaultMessage: t("messages.import.select_file") + " (.png .jpeg .jpg)",
      previewTemplate: "<div></div>",
    });

    const sendingFileImage = (file, xhr, formData) => {
      formData.append("id_luna_master_account", masterAccount.value);
    };

    const dropzoneImage = ref(null);
    const dropzoneImageFile = ref(null);
    const hasImage = computed(() => !!itemData.value.image);
    const faces = ref([]);
    const luna_server = ref();

    const hasFaces = ref(false);

    const previewUrl = ref("");
    const previewImg = ref(null);
    const isExtracting = ref(false);
    const extractionDone = ref(false);

    const fileName = computed(() =>
      dropzoneImageFile.value ? dropzoneImageFile.value.name : ""
    );

    const revokePreviewUrl = () => {
      if (previewUrl.value && previewUrl.value.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl.value);
      }
      previewUrl.value = "";
    };

    const clearLandmarks = () => {
      const canvas = document.querySelector(".luna-preview__frame #canvas");
      if (canvas) canvas.remove();
    };

    const addFileImage = (file) => {
      dropzoneImageFile.value = file;
      faces.value = [];
      hasFaces.value = false;
      extractionDone.value = false;
      clearLandmarks();
      revokePreviewUrl();
      previewUrl.value =
        file instanceof Blob ? URL.createObjectURL(file) : file.dataURL || "";

      if (masterAccount.value) {
        isExtracting.value = true;
        setTimeout(() => dropzoneImage.value.processQueue(), 500);
      }
    };

    const removeFileImage = () => {
      dropzoneImageFile.value = null;
      faces.value = [];
      hasFaces.value = false;
      extractionDone.value = false;
      isExtracting.value = false;
      clearLandmarks();
      revokePreviewUrl();
    };

    const clearImage = () => {
      if (dropzoneImage.value) {
        dropzoneImage.value.removeAllFiles();
      }
      removeFileImage();
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = (face) => {
      let dispatch;

      let form_data = new FormData();

      for (var key in face) {
        if (key == "list") continue;
        if (face[key] instanceof Object) form_data.append(key, JSON.stringify(face[key]));
        else form_data.append(key, face[key]);
      }

      face.list.forEach((list) => {
        form_data.append("list[]", list);
      });

      form_data.append("id_luna_master_account", masterAccount.value);
      form_data.append("image", dropzoneImageFile.value);

      dispatch = store.dispatch("luna_face/add", form_data);

      dispatch
        .then((response) => {
          if (response.success) {
            removeElement(face.sample_id);
            emit("next-step", {face: response.data, lunaMasterAccountId: masterAccount.value});
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState } = formValidation(resetData);

    const loadData = () => {
      listArray.value = [];
      masterAccount.value = null;
      dropzoneImageFile.value = null;
      dropzoneImage.value.removeAllFiles();

      if (props.imageUrl) {
        let mockFile = {
          size: 1234,
          name: "snapshot.jpeg",
          type: `image/jpeg`,
          kind: "image",
          upload: {
            filename: "snapshot.jpeg",
          },
          dataURL: props.imageUrl,
        };

        dropzoneImage.value.manuallyAddFile(mockFile, props.imageUrl, null, null, {
          dontSubstractMaxFiles: false,
          addToFiles: true,
        });
      }
    };

    function dataURLtoFile(dataurl, filename) {
      var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    }

    let submitFile = (image) => {
      let dispatch;

      let form_data = new FormData();

      form_data.append("file", image);
      form_data.append("id_luna_master_account", masterAccount.value);

      dispatch = store.dispatch("luna_face/extract", form_data);

      dispatch
        .then((response) => {
          if (response.success) {
            isExtracting.value = false;
            extractionDone.value = true;
            faces.value = response.data.faces || [];
            hasFaces.value = faces.value.length > 0;
            luna_server.value = response.data?.master_account?.api_server?.replace(
              "/6/",
              ""
            );
            renderLandmarks();
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    watch(masterAccount, (id) => {
      if (masterAccount.value && dropzoneImageFile.value) {
        isExtracting.value = true;
        if (props.imageUrl) {
          submitFile(dataURLtoFile(props.imageUrl));
        } else {
          setTimeout(() => dropzoneImage.value.processQueue(), 500);
        }
      }
      store
        .dispatch("luna_list/getAll", {
          luna_master_account: id,
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: "Error obteniendo la información",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    });

    let createCanvas = (class_name, translate = false) => {
      const htmlPlayer = document.querySelector(class_name);
      const canvasId = `canvas`;
      const oldCanvas = htmlPlayer.querySelector(`#${canvasId}`);

      if (oldCanvas) {
        return oldCanvas;
      }

      const canvas = document.createElement("canvas");
      canvas.id = canvasId;
      canvas.width = document.querySelector(`${class_name} img`).width;
      canvas.height = document.querySelector(`${class_name} img`).height;
      canvas.style.top = 0;
      canvas.style.left = 0;
      canvas.style.position = "absolute";
      canvas.style.zIndex = 100;
      if (translate) {
        canvas.style.left = "50%";
        canvas.style.transform = "translateX(-50%)";
      }
      htmlPlayer.appendChild(canvas);

      return canvas;
    };

    let drawLandmarks = (faces, class_name, translate) => {
      let img = document.querySelector(`${class_name} img`);
      let factor = img.height / img.naturalHeight;
      faces.forEach((face) => {
        const canvas = createCanvas(class_name, translate);
        const ctx = canvas.getContext("2d");
        const width = face.detection.rect.width * factor;
        const height = face.detection.rect.height * factor;
        const x = face.detection.rect.x * factor;
        const y = face.detection.rect.y * factor;
        ctx.strokeStyle = "lime";
        ctx.strokeRect(x, y, width, height);
      });
    };

    const renderLandmarks = () => {
      const img = previewImg.value;
      if (!img || !img.complete || !img.naturalHeight || !faces.value.length) return;
      clearLandmarks();
      drawLandmarks(faces.value, ".luna-preview__frame", false);
    };

    const success = (file, response) => {
      isExtracting.value = false;
      extractionDone.value = true;
      faces.value = (response.data && response.data.faces) || [];
      hasFaces.value = faces.value.length > 0;
      luna_server.value = response.data?.master_account?.api_server?.replace("/6/", "");
      renderLandmarks();
    };

    const error = (file, message, xhr) => {
      isExtracting.value = false;
      extractionDone.value = true;
      toast({
        component: ToastificationContent,
        props: {
          title: message.message,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const removeElement = (sample_id) => {
      faces.value = faces.value.filter((value, index, arr) => {
        if (value.sample_id == sample_id) return false;

        return true;
      });
    };

    onBeforeUnmount(() => {
      revokePreviewUrl();
    });

    return {
      itemData,
      onSubmit,
      listArray,
      refFormObserver,
      getValidationState,
      loadData,
      masterAccount,

      dropzoneOptions,
      dropzoneImage,
      addFileImage,
      removeFileImage,
      hasImage,

      hasFaces,
      sendingFileImage,
      success,
      error,
      resetData,

      faces,
      luna_server,

      previewUrl,
      previewImg,
      isExtracting,
      extractionDone,
      fileName,
      clearImage,
      renderLandmarks,

      removeElement,
    };
  },
};
</script>

<style lang="scss">
.dropzone-main {
  width: 100%;
  height: auto;
  min-height: 5vw;
  margin: auto;
  padding: 0;
  background-position: center;
  background-size: cover;
}

.dz-message {
  margin: 0 !important;
}

.luna-face-step .dropzone.dropzone-main {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  border: 1.5px dashed #d8d6de;
  border-radius: 0.428rem;
}

.dark-layout .luna-face-step .dropzone.dropzone-main {
  border-color: #404656;
}
</style>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.luna-face-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
  margin: 0.75rem 1rem;
  padding: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;

  .dark-layout & {
    border-color: $theme-dark-border-color;
    background-color: var(--second-dark);
  }
}

.luna-face-card__img {
  width: 9rem;
  max-width: 100%;
  height: auto;
  border-radius: 0.428rem;
  margin: auto;
}

.luna-face-card__fields {
  flex: 1 1 240px;
  min-width: 0;
}

.luna-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.luna-preview__frame {
  position: relative;
  align-self: center;
  max-width: 100%;
  line-height: 0;
}

.luna-preview__img {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 320px;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  object-fit: contain;

  .dark-layout & {
    border-color: $theme-dark-border-color;
  }
}

.luna-preview__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 0.428rem;
}

.luna-preview__overlay--warning {
  background: rgba(0, 0, 0, 0.55);
}

.luna-preview__overlay-icon {
  color: #ff9f43;
}

.luna-preview__overlay-text {
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.3;
  text-align: center;
  padding: 0 0.75rem;
}

.luna-preview__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.luna-preview__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  color: #6e6b7b;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}
</style>
